import React from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

import { CoachProfileModel } from "app/models/scheduling/CoachProfileModel";
import { PackageCredit } from "app/utils/creditBundles";

import styles from "./CoachCard.module.scss";
import CoachAvatar from "./CoachAvatar";
import { bookableRates, coachAcceptsPackage, coachMetaLine } from "./coachMeta";
import { NextSlot, nextSlotLabel } from "./coachNextSlot";
import useCalendarPreferences from "app/utils/hooks/useCalendarPreferences";
import { timePattern } from "app/utils/timePattern";

interface Props {
  coach: CoachProfileModel;
  packageCredits?: Map<number, PackageCredit>;
  /** The coach's soonest bookable time; undefined until it has been looked up. */
  nextSlot?: NextSlot;
  nextSlotLoading?: boolean;
  onSelect: (coach: CoachProfileModel) => void;
}

const CoachCard: React.FC<Props> = ({
  coach,
  packageCredits,
  nextSlot,
  nextSlotLoading = false,
  onSelect,
}) => {
  const { t } = useTranslation();
  const { preferences } = useCalendarPreferences();

  const metaLine = coachMetaLine(coach);
  const showPackageRibbon = coachAcceptsPackage(coach, packageCredits);

  /**
   * CFTB-3478: a coach with nothing to sell and a coach with nothing free are not
   * the same thing, and the card said the same of both. "No upcoming availability"
   * reads as "ask again next week" — wrong for a coach who has no booking options
   * at all, and Madina's point: a coach can have sessions and no free times, which
   * is the case that sentence is for.
   *
   * Four states, and the chip keeps its shape through all of them so the cards in a
   * row do not change height as availability arrives.
   */
  const availability = bookableRates(coach).length === 0
    ? t("No booking options")
    : nextSlotLoading
      ? t("Checking availability…")
      : nextSlot
        ? nextSlotLabel(
            nextSlot,
            DateTime.local(),
            {
              today: (time) => t("Next: Today, {{time}}", { time }),
              tomorrow: (time) => t("Next: Tomorrow, {{time}}", { time }),
              onDay: (day, time) => t("Next: {{day}}, {{time}}", { day, time }),
            },
            timePattern(preferences?.timeFormat),
          )
        : t("No upcoming availability");

  return (
    <div className={showPackageRibbon ? styles.card_with_package : styles.card_plain}>
      {showPackageRibbon && (
        <div className={styles.package_ribbon}>
          <i className="ico ico-star" />
          <span>{t("You can apply your Package")}</span>
        </div>
      )}

      <button
        type="button"
        className={styles.card_body}
        onClick={() => onSelect(coach)}
        data-testid="coach-card"
      >
        <CoachAvatar coach={coach} className={styles.avatar} fontSize={32} />

        <div className={styles.details}>
          <div className={styles.name}>{coach.fullName}</div>
          {metaLine !== "" && <div className={styles.meta}>{metaLine}</div>}
          <div
            className={styles.session_types}
            data-testid="coach-card-next-slot"
          >
            <i className="ico ico-time" />
            <span>{availability}</span>
          </div>
        </div>

        <i className={`ico ico-chevron ${styles.chevron}`} />
      </button>
    </div>
  );
};

export default CoachCard;
